import { useEffect, useState } from "react";

import { TableMetadata, TablePagination, TablePaginationProps, SortState, IconWrapper, ColumnSortEnum } from "@king-ict/eupisi-ui/components";
import { Table as BSTable } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { ReactComponent as ArrowIcon } from "../../icon/Arrow.svg";
import { ReactComponent as ArrowSmallIcon } from "../../icon/ArrowSmall.svg";
import { ReactComponent as SortingArrow} from "../../icon/SortingArrow.svg";
import styles from "./MasterDetailTable.module.scss";

export interface MasterDetailTableProps {
    readonly masterMetadata: TableMetadata<any>;
    readonly masterData: any[];
    readonly detailMetadata: TableMetadata<any>;    
    readonly detailPropertyColumn: string;
    readonly pagination: boolean;
    readonly paginationProps?: TablePaginationProps;
    readonly columnSort?: SortState;
    readonly noHover?: boolean;    
    readonly sortBy?: (column: number) => void;
}

export const MasterDetailTable = (props: MasterDetailTableProps) => {    
    const [tableState, setTableState] = useState<MasterDetailTableProps>(props);
    const { t } = useTranslation();
    
    useEffect(() => {
        setTableState(props);
        setDetailsVisible(createDefaultDetailsVisible());
      }, [props]);

    function getColor(color?: string) {        
        if (color) return styles[color];
        return "";
    }

    const handleSortClick = (column?: number) => (e: any) => { 
        e.preventDefault();
        if (column) {
          tableState.sortBy && tableState.sortBy(column);
        }
    };    

    const detailsVisibleDictionary = (key: number) => {        

        var newValue = !detailsVisible[key];

        const newDict: any = {
            ...detailsVisible,
            [key]: newValue
        }
        setDetailsVisible(newDict);
    };    

    const createDefaultDetailsVisible = () : any =>{        

        var dict: any = {};

        if(tableState.masterMetadata)
        {
            tableState.masterData.forEach((md) => {                                
                dict[md[tableState.masterMetadata.rowKey]] = false;
            });
        }
        return dict;
    };
    
    const [ detailsVisible, setDetailsVisible] = useState(() => createDefaultDetailsVisible());


    const getSortingComponent = (column: number) => {
        return (
            <div style={{
                display: "flex",
                flexDirection: "column",
                marginLeft: "8px",
                alignSelf: "center",
              }}>
                <IconWrapper color="black-color">
                    <SortingArrow
                        aria-label={t("accessibility.sortDesc")}
                        opacity={                            
                            tableState.columnSort != null &&
                            tableState.columnSort.column === column &&
                            tableState.columnSort.sortType === ColumnSortEnum.DESC
                                ? "1"
                                : "0.3"
                        }
                    />                    
                </IconWrapper>
                <IconWrapper color="black-color">
                    <SortingArrow
                        aria-label={t("accessibility.sortAsc")}
                        opacity={
                            tableState.columnSort != null &&
                            tableState.columnSort.column === column &&
                            tableState.columnSort.sortType === ColumnSortEnum.ASC
                                ? "1"
                                : "0.3"
                        }
                        transform="rotate(180)"
                    />
                </IconWrapper>
            </div>
        );
    };

    const getDetailHeader = (masterRowKey: number) => {
        return(
            
            <tr>            
                {tableState.detailMetadata.columns.map((column) => {
                    return (
                        <th 
                            className={column.headerClassName}
                            key={column.columnKey}                        
                        >
                            <div>
                                {column.header}
                            </div>
                        </th>
                    );
                })}                
            </tr>            
        );
    }

    const getDetailRow = (row: any, masterDataIndex: number) => {
        return (
            <>
                <tr key= {`${tableState.masterData[masterDataIndex][tableState.masterMetadata.rowKey]}_${row[tableState.detailMetadata.rowKey]}`}>                    
                    {tableState.detailMetadata.columns.map((column, index) => {
                        return (
                            <td key={`${tableState.masterData[masterDataIndex][tableState.masterMetadata.rowKey]}_${row[tableState.detailMetadata.rowKey]}_${column.columnKey}`}>
                                {typeof column.accessor === "function" 
                                    ? column.accessor(row)
                                    : row[column.accessor]}
                            </td>
                        );
                    })}
                </tr>
            </>
        );
    };

    const getDetailData = (masterDataIndex: number) => {
        if(tableState.masterData && tableState.masterData[masterDataIndex] && tableState.masterData[masterDataIndex][tableState.detailPropertyColumn]){
            return tableState.masterData[masterDataIndex][tableState.detailPropertyColumn].map((rowData : any, i: number) => {
                return getDetailRow(rowData, masterDataIndex);
            });
        }
        return <></>;        
    };

    const toggleExpandClicked = (masterRowKey: number) => {    
        detailsVisibleDictionary(masterRowKey);        
    };

    const getDetailDiv = (masterDataRowKey: number, masterDataIndex: number) =>  
    {
        if(detailsVisible[masterDataRowKey] == true){
            return (            
                <td colSpan={tableState.masterMetadata.columns.length}>
                    <div className={styles.detailReactTableWrap}>
                        <BSTable className={styles.reactTable}>                
                        <thead>
                            {getDetailHeader(masterDataRowKey)}
                        </thead>
                            <tbody>
                                {getDetailData(masterDataIndex)}                
                            </tbody>
                        </BSTable>

                    </div>
                </td>            
            )
        }
        return <></>;
    }

    const getBodyRow = (row: any, masterDataIndex: number) => {
        return (
            <>
                <tr key={row[tableState.masterMetadata.rowKey]}>
                    {tableState.masterMetadata.columns.map((column, index) => {
                        return (
                            <td key={`${row[tableState.masterMetadata.rowKey]}_${column.columnKey}`}
                                className={`${styles[column.cellClassName ?? ""]} ${getColor(
                                    column.color
                                )} ${column.onCellClick != null ? styles.clickable : ""}`}
                                onClick={() => {
                                    if (column.onCellClick != null) {
                                        column.onCellClick(row);
                                    }
                                }}>
                                    {typeof column.accessor === "function"
                                        ? column.accessor(row)
                                        : row[column.accessor]}
                            </td>
                        );
                    })} 
                    <td>                        
                        <div className={`${detailsVisible[row[tableState.masterMetadata.rowKey]]===true ? styles.expanded : ""}`}>
                            <IconWrapper>
                                <ArrowIcon onClick={() => toggleExpandClicked(row[tableState.masterMetadata.rowKey])}/>
                            </IconWrapper>
                        </div>
                    </td>               
                </tr>                    
                {getDetailDiv(row[tableState.masterMetadata.rowKey], masterDataIndex)}
            </>
        );
    };

    

    const getBodyData = () => {
        if(tableState.masterData){
            return tableState.masterData.map((rowData, i) => {
                return getBodyRow(rowData, i);
            });
        }
        return <></>;
    };      

    return(
        <>
            <div className={styles.reactTableWrap}>
                <BSTable className={styles.reactTable}>
                    <thead>
                        <tr>
                            {tableState.masterMetadata.columns.map((column) => {
                                return (
                                    <th
                                        className={column.headerClassName}
                                        key={column.columnKey}
                                        onClick={handleSortClick(column.sortBy)}
                                    >
                                        <div className={column.sortBy ? styles.sortableHeader : ""}>
                                            {column.header}{" "}
                                            {column.sortBy ? (
                                                getSortingComponent(column.sortBy)
                                            ) : (<></>)}                                            
                                        </div>
                                    </th>
                                );
                            })}
                            <th key="extend">                                
                            </th>
                        </tr>
                    </thead>
                    <tbody>{getBodyData()}</tbody>
                </BSTable>
            </div>
            <div className={styles.tablePaginationWrap}>
                {tableState.pagination && tableState.paginationProps ? (
                    <TablePagination {...tableState.paginationProps} />
                ) : (
                    <></>
                )}
            </div>
        </>
    );
};